import {
  ManagerCompaniesActionTypes,
  ManagerCompaniesActions
} from '@appStore/managers/actions/manager-companies.actions';
import {
  ManagerCompany,
  ManagerCompanyUser,
  CustomerManagerCompany,
  ManagerCustomerUser,
  ManagerJobLockStatus
} from '@model';

export interface State {
  loading: boolean;
  loaded: boolean;
  data: ManagerCompany[];
  selected: ManagerCompany;
  usersLoading: boolean;
  usersLoaded: boolean;
  users: ManagerCompanyUser[];
  selectedUser: ManagerCompanyUser;
  userRemoving: boolean;
  userRemoved: boolean;
  removing: boolean;
  removed: boolean;
  customers: CustomerManagerCompany[];
  customersLoading: boolean;
  customersLoaded: boolean;
  registered: boolean;
  registeredLoaded: boolean;
  registeredLoading: boolean;
  userAdded: boolean;
  userAdding: boolean;
  userExclusiveAdministrator: boolean;
  userExclusiveAdministratorLoading: boolean;
  userExclusiveAdministratorLoaded: boolean;
  userCustomers: ManagerCustomerUser[];
  userCustomersLoading: boolean;
  userCustomersLoaded: boolean;
  selectedCustomers: string[];
  userUpdated: boolean;
  userUpdating: boolean;
  userLockStatus: ManagerJobLockStatus;
  userLockStatusLoaded: boolean;
  userLockStatusLoading: boolean;

  usersLockStatus: ManagerJobLockStatus[];
  usersLockStatusLoaded: boolean;
  usersLockStatusLoading: boolean;

  deleteLockStatus: ManagerJobLockStatus;
  deleteLockStatusLoaded: boolean;
  deleteLockStatusLoading: boolean;

  lockedManagerUsers: ManagerJobLockStatus[];

  tabSelected: number;
}

export const initialState: State = {
  loading: false,
  loaded: false,
  data: [],
  selected: null,
  usersLoading: false,
  usersLoaded: false,
  users: [],

  userRemoving: false,
  userRemoved: false,
  removing: false,
  removed: false,

  customers: [],
  customersLoading: false,
  customersLoaded: false,

  registered: false,
  registeredLoaded: false,
  registeredLoading: false,

  userAdded: false,
  userAdding: false,
  userExclusiveAdministrator: false,
  userExclusiveAdministratorLoading: false,
  userExclusiveAdministratorLoaded: false,
  userCustomers: [],
  userCustomersLoading: false,
  userCustomersLoaded: false,
  selectedCustomers: null,
  selectedUser: null,
  userUpdated: false,
  userUpdating: false,

  userLockStatus: null,
  userLockStatusLoaded: false,
  userLockStatusLoading: false,

  usersLockStatus: [],
  usersLockStatusLoaded: false,
  usersLockStatusLoading: false,

  deleteLockStatus: null,
  deleteLockStatusLoaded: false,
  deleteLockStatusLoading: false,

  lockedManagerUsers: [],

  tabSelected: 0
};

export function reducer(state = initialState, action: ManagerCompaniesActions): State {
  switch (action.type) {
    case ManagerCompaniesActionTypes.ManagerCompaniesLoadData:
      return { ...state, loading: true, loaded: false };

    case ManagerCompaniesActionTypes.ManagerCompaniesLoadSuccess:
      return { ...state, data: action.payload, loading: false, loaded: true };

    case ManagerCompaniesActionTypes.ManagerCompaniesLoadFail:
      return { ...state, data: [], loading: false, loaded: false };

    case ManagerCompaniesActionTypes.ManagerCompanySelect:
      return { ...state, selected: action.payload };

    case ManagerCompaniesActionTypes.ManagerCompaniesUsersLoadData:
      return { ...state, usersLoading: true, usersLoaded: false };

    case ManagerCompaniesActionTypes.ManagerCompaniesUsersLoadSuccess:
      return { ...state, users: action.payload, usersLoading: false, usersLoaded: true };

    case ManagerCompaniesActionTypes.ManagerCompaniesUsersLoadFail:
      return { ...state, users: [], usersLoading: false, usersLoaded: false };

    case ManagerCompaniesActionTypes.ManagerCompanyUserSelect:
      return { ...state, selectedUser: action.payload };

    case ManagerCompaniesActionTypes.UpdateManagerCompanyUser:
      return { ...state, userUpdating: true, userUpdated: false };
    case ManagerCompaniesActionTypes.UpdateManagerCompanyUserSuccess:
      const updatedItem = action.payload;
      return {
        ...state,
        userUpdating: false,
        userUpdated: true,
        users: state.users.map(item =>
          item.userId === updatedItem.userId && item.managerCompanyId === updatedItem.managerCompanyId
            ? updatedItem
            : item
        )
      };
    case ManagerCompaniesActionTypes.UpdateManagerCompanyUserFail:
      return { ...state, users: [], userUpdating: false, userUpdated: false };

    case ManagerCompaniesActionTypes.BulkUpdateManagerCompanyUser:
      return { ...state, userUpdating: true, userUpdated: false };
    case ManagerCompaniesActionTypes.BulkUpdateManagerCompanyUserSuccess:
      return {...state, userUpdating: false, userUpdated: true, userLockStatus: action.payload};
    case ManagerCompaniesActionTypes.BulkUpdateManagerCompanyUserFail:
      return { ...state, users: [], userUpdating: false, userUpdated: false };
    case ManagerCompaniesActionTypes.ClearBulkUpdateManagerCompanyUserState:
      return {...state, userUpdating: false, userUpdated: false, userLockStatus: null};

    case ManagerCompaniesActionTypes.ManagerCompanyRemove:
      return { ...state, removing: true, removed: false };
    case ManagerCompaniesActionTypes.ManagerCompanyRemoveSuccess:
      const removedCompany = action.payload;
      return {
        ...state,
        removing: false,
        removed: true,
        data: state.data.filter(item => item.id !== removedCompany.id)
      };
    case ManagerCompaniesActionTypes.ManagerCompanyRemoveFail:
      return { ...state, removing: false, removed: false };

    case ManagerCompaniesActionTypes.ManagerCompanyCustomersLoadData:
      return { ...state, customersLoading: true, customersLoaded: false };
    case ManagerCompaniesActionTypes.ManagerCompanyCustomersLoadSuccess:
      return {
        ...state,
        customers: action.payload,
        customersLoading: false,
        customersLoaded: true
      };
    case ManagerCompaniesActionTypes.ManagerCompanyCustomersLoadFail:
      return {
        ...state,
        customers: [],
        customersLoading: false,
        customersLoaded: false
      };

    case ManagerCompaniesActionTypes.ManagerCompanyUserCustomersLoadData:
      return { ...state, userCustomersLoading: true, userCustomersLoaded: false };
    case ManagerCompaniesActionTypes.ManagerCompanyUserCustomersLoadSuccess:
      return {
        ...state,
        userCustomers: action.payload,
        userCustomersLoading: false,
        userCustomersLoaded: true
      };
    case ManagerCompaniesActionTypes.ManagerCompanyUserCustomersLoadFail:
      return {
        ...state,
        userCustomers: [],
        userCustomersLoading: false,
        userCustomersLoaded: false
      };

    case ManagerCompaniesActionTypes.IsManagerCompanyUserExclusiveAdministrator:
      return {
        ...state,
        userExclusiveAdministratorLoaded: false,
        userExclusiveAdministratorLoading: true,
        userExclusiveAdministrator: false
      };
    case ManagerCompaniesActionTypes.IsManagerCompanyUserExclusiveAdministratorFail:
      return { ...state, userExclusiveAdministratorLoaded: false, userExclusiveAdministratorLoading: false };
    case ManagerCompaniesActionTypes.IsManagerCompanyUserExclusiveAdministratorSuccess:
      return {
        ...state,
        userExclusiveAdministratorLoaded: true,
        userExclusiveAdministratorLoading: false,
        userExclusiveAdministrator: action.payload
      };

    case ManagerCompaniesActionTypes.SetCustomerSelection:
      return {
        ...state,
        selectedCustomers: [...action.payload]
      };
    case ManagerCompaniesActionTypes.IsManagerCompanyUserLockedForUpdate:
      return {
        ...state,
        userLockStatusLoaded: false,
        userLockStatusLoading: true,
        userLockStatus: null
      };
    case ManagerCompaniesActionTypes.IsManagerCompanyUserLockedForUpdateSuccess:
      return { ...state, userLockStatusLoaded: true, userLockStatusLoading: false, userLockStatus: action.payload };
    case ManagerCompaniesActionTypes.IsManagerCompanyUserLockedForUpdateFail:
      return {
        ...state,
        userLockStatusLoaded: false,
        userLockStatusLoading: false
      };

    case ManagerCompaniesActionTypes.ManagerCompanyUsersLockedForUpdateLoadData:
      return {
        ...state,
        usersLockStatusLoaded: false,
        usersLockStatusLoading: true,
        usersLockStatus: null
      };
    case ManagerCompaniesActionTypes.ManagerCompanyUsersLockedForUpdateLoadSuccess:
      return { ...state, usersLockStatusLoaded: true, usersLockStatusLoading: false, usersLockStatus: action.payload };
    case ManagerCompaniesActionTypes.ManagerCompanyUsersLockedForUpdateLoadFail:
      return {
        ...state,
        usersLockStatusLoaded: false,
        usersLockStatusLoading: false
      };
    case ManagerCompaniesActionTypes.ClearManagerCompanyUsersLockedForUpdateState:
      return {
        ...state,
        usersLockStatusLoaded: false,
        usersLockStatusLoading: false,
        usersLockStatus: null
          };

    case ManagerCompaniesActionTypes.ManagerUserLockUpdate:
      return {
        ...state,
        lockedManagerUsers: [...action.payload]
      };

    case ManagerCompaniesActionTypes.SetPrimaryContactSuccess:
      const newPrimaryContact = action.payload;

      return {
        ...state,
        users: [
          ...state.users.map(x => {
            return { ...x, isPrimaryContactPerson: x.userId === newPrimaryContact.userId };
          })
        ]
      };

    case ManagerCompaniesActionTypes.ManagerCompanyTabChanged:
      return { ...state, tabSelected: action.payload };

    // Lock for Delete - ManagerCompanyUser
    case ManagerCompaniesActionTypes.LockManagerCompanyUserForDelete:
      return { ...state, deleteLockStatusLoading: true, deleteLockStatusLoaded: false };

    case ManagerCompaniesActionTypes.LockManagerCompanyUserForDeleteSuccess:
      return { ...state, deleteLockStatusLoading: false, deleteLockStatusLoaded: true, deleteLockStatus: action.payload };

    case ManagerCompaniesActionTypes.LockManagerCompanyUserForDeleteFail:
      return { ...state, deleteLockStatusLoading: false, deleteLockStatusLoaded: false, deleteLockStatus: null };

    case ManagerCompaniesActionTypes.ClearLockManagerCompanyUserForDeleteState:
        return {
          ...state,
          deleteLockStatusLoaded: false,
          deleteLockStatusLoading: true,
          deleteLockStatus: null
            };
  }

  return state;
}

export const getManagerCompanies = (state: State) => state.data;
export const getManagerCompaniesLoading = (state: State) => state.loading;
export const getManagerCompaniesLoaded = (state: State) => state.loaded;
export const getNumberOfManagerCompanies = (state: State) => (state.loaded ? state.data.length : 0);

export const getManagerCompanySelected = (state: State) => state.selected;

export const getManagerCompaniesUsers = (state: State) => state.users;
export const getManagerCompaniesUsersLoading = (state: State) => state.usersLoading;
export const getManagerCompaniesUsersLoaded = (state: State) => state.usersLoaded;

export const getManagerCompanyUserSelected = (state: State) => state.selectedUser;

export const getManagerCompanyUserUpdated = (state: State) => state.userUpdated;
export const getBulkManagerCompanyUser = (state: State) => state.userLockStatus;
export const getBulkManagerCompanyUserUpdated = (state: State) => state.userUpdated;

export const getManagerCompanyRemoved = (state: State) => state.removed;

export const getManagerCompanyCustomers = (state: State) => state.customers;
export const getManagerCompanyCustomersLoading = (state: State) => state.customersLoading;
export const getManagerCompanyCustomersLoaded = (state: State) => state.customersLoaded;

export const getManagerCompanyUserCustomers = (state: State) => state.userCustomers;
export const getManagerCompanyUserCustomersLoading = (state: State) => state.userCustomersLoading;
export const getManagerCompanyUserCustomersLoaded = (state: State) => state.userCustomersLoaded;

export const getIsManagerCompanyUserExclusiveAdministrator = (state: State) => state.userExclusiveAdministrator;
export const getIsManagerCompanyUserExclusiveAdministratorLoaded = (state: State) =>
  state.userExclusiveAdministratorLoaded;

export const getIsManagerCompanyUserLockedForUpdate = (state: State) => state.userLockStatus;
export const getIsManagerCompanyUserLockedForUpdateLoaded = (state: State) => state.userLockStatusLoaded;

export const getManagerCompanyUserLockedForDelete = (state: State) => state.deleteLockStatus;
export const getManagerCompanyUserLockedForDeleteLoaded = (state: State) => state.deleteLockStatusLoaded;

export const getManagerCompanyUsersLockedForUpdate = (state: State) => state.usersLockStatus;
export const getManagerCompanyUsersLockedForUpdateLoaded = (state: State) => state.usersLockStatusLoaded;

export const getManagerCompanyCustomersSelected = (state: State) => state.selectedCustomers;

export const getManagerCompanySelectedTab = (state: State) => state.tabSelected;

export const getLockedManagerUsers = (state: State) => state.lockedManagerUsers;
